import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection as firestoreCollection, doc, getDoc, getDocs } from 'firebase/firestore';
import { functions, db } from '../firebase';
import {
  Typography, TextField, MenuItem, Alert, Box, Paper, Table, TableHead,
  TableRow, TableCell, TableBody,
} from '@mui/material';

const listFinancialSupport = httpsCallable(functions, 'listFinancialSupport');

const LEGACY_VIEW_OPTIONS = [{ id: 'sp26', mode: 'legacy', label: 'sp26 (legacy)' }];

const currency = (n) => `$${Number(n || 0).toFixed(2)}`;

// Who owes what: for each locality, the total financial support their
// registrants received (subsidized by the conference) and the individual
// registrants making up that total. VIEWER_LOCALITY sees only their own
// locality - the backend (listFinancialSupport) enforces that regardless of
// what this UI does, so there's nothing to additionally restrict here.
export default function PaymentViewPanel({ scopedLocality }) {
  const [viewOptions, setViewOptions] = useState(LEGACY_VIEW_OPTIONS);
  const [viewingId, setViewingId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [activeSnap, confsSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'active')),
          getDocs(firestoreCollection(db, 'conferences')),
        ]);
        const newOptions = confsSnap.docs.map((d) => ({ id: d.id, mode: 'new', label: d.id }));
        const options = [...newOptions, ...LEGACY_VIEW_OPTIONS];
        setViewOptions(options);
        const active = activeSnap.data()?.conferenceId;
        setViewingId(active || options[0]?.id || null);
      } catch (error) {
        console.error('Failed to load conference list:', error);
        setViewOptions(LEGACY_VIEW_OPTIONS);
        setViewingId(LEGACY_VIEW_OPTIONS[0].id);
      }
    })();
  }, []);

  const currentOption = viewOptions.find((o) => o.id === viewingId);

  useEffect(() => {
    if (!viewingId) return;
    (async () => {
      setLoading(true);
      setMessage(null);
      try {
        const params = currentOption?.mode === 'legacy' ? { collection: viewingId } : { conferenceId: viewingId };
        const res = await listFinancialSupport(params);
        setItems(res.data?.items || []);
      } catch (error) {
        console.error('Failed to load financial support data:', error);
        setItems([]);
        setMessage({ type: 'error', text: error.message });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingId, viewOptions]);

  const groups = useMemo(() => {
    const byLocality = new Map();
    for (const item of items) {
      if (!byLocality.has(item.locality)) byLocality.set(item.locality, []);
      byLocality.get(item.locality).push(item);
    }
    return [...byLocality.entries()]
      .map(([locality, registrants]) => ({
        locality,
        total: registrants.reduce((sum, r) => sum + r.financialSupportAmount, 0),
        registrants,
      }))
      .sort((a, b) => b.total - a.total);
  }, [items]);

  const grandTotal = useMemo(() => items.reduce((sum, r) => sum + r.financialSupportAmount, 0), [items]);

  return (
    <Box sx={{ maxWidth: 900 }}>
      {scopedLocality && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You're viewing financial support for <strong>{scopedLocality}</strong> only.
        </Alert>
      )}
      <Box sx={{ mb: 2, maxWidth: 300 }}>
        <TextField
          select fullWidth size="small" label="Viewing conference"
          value={viewingId || ''}
          onChange={(e) => setViewingId(e.target.value)}
        >
          {viewOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>{o.label || o.id}</MenuItem>
          ))}
        </TextField>
      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

      {loading ? <Typography>Loading...</Typography> : groups.length === 0 ? (
        <Typography color="text.secondary">No financial support recorded for this conference.</Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Total across {groups.length === 1 ? 'this locality' : `${groups.length} localities`}: <strong>{currency(grandTotal)}</strong>
          </Typography>
          {groups.map((group) => (
            <Paper key={group.locality} variant="outlined" sx={{ mb: 2, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                <Typography variant="h6">{group.locality}</Typography>
                <Typography variant="h6" color="error.main">{currency(group.total)}</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Registrant</TableCell>
                    <TableCell>Support source</TableCell>
                    <TableCell>Serving One</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.registrants.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.firstName} {r.lastName}</TableCell>
                      <TableCell>{r.financialSupportSource}</TableCell>
                      <TableCell>{r.servingOneName}{r.servingOneEmail ? ` (${r.servingOneEmail})` : ''}</TableCell>
                      <TableCell align="right">{currency(r.financialSupportAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ))}
        </>
      )}
    </Box>
  );
}
